import { RestrictionOptionResponseMethod } from '@/types/api/mypage/services/payment/restriction/option';
import { RestrictionJudgeAccountTransferResponseMethod } from '@/types/api/mypage/services/payment/restriction/judgeAccountTransfer';

/**
 * 指定した支払方法が使用可能かどうかを確認する
 * 規制情報 API が返す method 配列から対象支払方法を引き、選択不可かどうかを共通判定する。
 * @param type 支払方法
 * @param methods 規制情報
 * @returns 使用不可な場合は、trueを返却する。それ以外は、falseを返却する
 */
const isRestrict = ({
  type,
  methods,
}: {
  type: string;
  methods?: Array<RestrictionOptionResponseMethod | RestrictionJudgeAccountTransferResponseMethod>;
}): boolean => {
  if (!methods) {
    // 規制情報未取得時は制限ありと断定せず、呼び出し元で通常表示できるよう false にする。
    return false;
  }
  const method = methods.find((e) => e.type === type);
  if (!method) {
    // 対象支払方法の定義が返っていない場合は、個別制限なしとして扱う。
    return false;
  }
  // `is_restrict = false` が「利用不可」を表す API 仕様なので、そのまま真偽を反転せず返す。
  return method.is_restrict === false;
};

/**
 * 「支払方法 = クレジットカード」が使用可能かどうかを確認する
 * 支払方法ごとの type 文字列を呼び出し元に書かせないための薄いラッパー。
 * @param methods 規制情報（Option API または SMCC API）
 * @returns 使用不可な場合は、trueを返却する。それ以外は、falseを返却する
 */
export const isRestrictCreditCard = ({
  methods,
}: {
  methods?: Array<RestrictionOptionResponseMethod | RestrictionJudgeAccountTransferResponseMethod>;
}): boolean => {
  return isRestrict({ type: 'credit_card', methods });
};

/**
 * 「支払方法 = 口座振替」が使用可能かどうかを確認する
 * 共通判定へ type だけを渡し、口座振替可否判定を読みやすくする。
 * @param methods 規制情報（Option API または SMCC API）
 * @returns 使用不可な場合は、trueを返却する。それ以外は、falseを返却する
 */
export const isRestrictAccountTransfer = ({
  methods,
}: {
  methods?: Array<RestrictionOptionResponseMethod | RestrictionJudgeAccountTransferResponseMethod>;
}): boolean => {
  return isRestrict({ type: 'account_transfer', methods });
};

/**
 * 「支払方法 = NTT請求（電話料金合算）」が使用可能かどうかを確認する
 * NTT 請求の規制有無を画面分岐で直接使える形に整える。
 * @param methods 規制情報（Option API または SMCC API）
 * @returns 使用不可な場合は、trueを返却する。それ以外は、falseを返却する
 */
export const isRestrictNtt = ({
  methods,
}: {
  methods?: Array<RestrictionOptionResponseMethod | RestrictionJudgeAccountTransferResponseMethod>;
}): boolean => {
  return isRestrict({ type: 'ntt', methods });
};

/**
 * 「支払方法 = ソフトバンク請求（電話料金合算）」が使用可能かどうかを確認する
 * キャリア決済別の表示可否分岐を統一するための専用ラッパー。
 * @param methods 規制情報（Option API または SMCC API）
 * @returns 使用不可な場合は、trueを返却する。それ以外は、falseを返却する
 */
export const isRestrictSoftbank = ({
  methods,
}: {
  methods?: Array<RestrictionOptionResponseMethod | RestrictionJudgeAccountTransferResponseMethod>;
}): boolean => {
  return isRestrict({ type: 'sb', methods });
};

/**
 * 「支払方法 = au：auかんたん決済」が使用可能かどうかを確認する
 * au 系決済の規制有無を意味ベースで参照できるようにする。
 * @param methods 規制情報（Option API または SMCC API）
 * @returns 使用不可な場合は、trueを返却する。それ以外は、falseを返却する
 */
export const isRestrictAu = ({
  methods,
}: {
  methods?: Array<RestrictionOptionResponseMethod | RestrictionJudgeAccountTransferResponseMethod>;
}): boolean => {
  return isRestrict({ type: 'au', methods });
};
